import { Button, ProgressCircle } from '@react-spectrum/s2';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import {
	TYPE_TITLES,
	isArtifactActionable,
} from '@/components/artifact/panelMeta';
import { useEnsureSetupSnapshot } from '@/components/artifact/useEnsureSetupSnapshot';
import { useGoToNextStep } from '@/components/artifact/useGoToNextStep';
import { useJobActivation } from '@/components/artifact/useJobActivation';
import { useStepSubmitted } from '@/components/artifact/useStepSubmitted';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import { SubredditMonitorEditor } from '@/spaces/chat/panels/SubredditMonitorEditor';
import { trackEvent } from '@/utils/fullstory';

import type { SubredditMonitor } from '../../../../../bridge/types/artifact.ts';
import type { PanelProcess } from '../../../../../bridge/types/state.ts';
import { hasFunnelSibling } from '../../../../../bridge/utils/setupCardItems.ts';

interface SubredditMonitorPanelProps {
	artifact: SubredditMonitor;
	processId?: string;
	processStatus?: PanelProcess['status'];
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

type InFlightAction = 'paused' | 'resumed';
type ActionTaken = InFlightAction | 'failed' | null;

const PROGRESS_LABEL: Record<InFlightAction, string> = {
	paused: 'Pausing monitor…',
	resumed: 'Resuming monitor…',
};

const ACTION_TIMEOUT_MS = 30_000;

export const SubredditMonitorPanel = ({
	artifact,
	processId,
	processStatus,
	onBack,
	onClose,
	style,
}: SubredditMonitorPanelProps) => {
	const bridgeApi = useBridgeApi();
	const isProcess = processId != null;
	useEnsureSetupSnapshot(artifact.process_id);
	const goToNextItem = useGoToNextStep();
	const { handleActivate, activatedView } = useJobActivation({
		artifact,
		title: 'Subreddit monitor activated',
		summary:
			"I'll watch the subreddits you picked and surface matches on your home screen.",
		onClose,
	});

	// Funnel sibling vs standalone (setup-card flow only — isProcess is the
	// post-activation job view). When this monitor is one of several items in a
	// proposed process, confirming it marks the setup item complete and advances
	// rather than activating the whole process. When it's the sole child, keep
	// the direct activate.
	const snapshot = artifact.process_id
		? (bridgeApi.user_state?.process_setup_snapshots?.[artifact.process_id] ??
			null)
		: null;
	const isFunnelSibling = !isProcess && hasFunnelSibling(snapshot);
	const { submittedView, markSubmitted } = useStepSubmitted({
		title: 'Subreddit monitor confirmed',
		summary: 'Ready for the next step in your campaign setup.',
		onClose,
		onGoToNextStep: () => {
			if (artifact.process_id) goToNextItem(artifact.process_id, artifact.id);
		},
	});

	const [actionTaken, setActionTaken] = useState<ActionTaken>(null);
	const timeoutRef = useRef<number | null>(null);

	const clearActionTimeout = useCallback(() => {
		if (timeoutRef.current != null) {
			window.clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	// When process-wrapped, processStatus is the authoritative signal:
	// 'failed' immediately surfaces the error pill; settling out of
	// 'processing' clears any in-flight spinner.
	useEffect(() => {
		if (!isProcess) {
			clearActionTimeout();
			setActionTaken(null);
			return;
		}
		if (processStatus === 'failed') {
			clearActionTimeout();
			setActionTaken('failed');
		} else if (processStatus !== 'processing') {
			clearActionTimeout();
			setActionTaken(null);
		}
	}, [isProcess, artifact.status, processStatus, clearActionTimeout]);

	useEffect(() => clearActionTimeout, [clearActionTimeout]);

	const armTimeout = useCallback(
		(inFlight: InFlightAction) => {
			clearActionTimeout();
			timeoutRef.current = window.setTimeout(() => {
				setActionTaken((prev) => (prev === inFlight ? 'failed' : prev));
				timeoutRef.current = null;
			}, ACTION_TIMEOUT_MS);
		},
		[clearActionTimeout],
	);

	const subreddits = artifact.content.subreddits ?? [];
	const watchTopic = artifact.content.watch_topic ?? '';

	const handleSubmitStep = useCallback(() => {
		if (!artifact.process_id) return;
		trackEvent('Subreddit Monitor Submitted', { artifact_id: artifact.id });
		bridgeApi.sendAction({
			type: 'do_mark_setup_item_completed',
			process_id: artifact.process_id,
			item_id: artifact.id,
		});
		markSubmitted();
	}, [bridgeApi, artifact.id, artifact.process_id, markSubmitted]);

	const handlePause = useCallback(() => {
		trackEvent('Process Paused', {
			artifact_id: artifact.id,
			...(processId ? { process_id: processId } : {}),
			...(processStatus ? { process_status: processStatus } : {}),
		});
		setActionTaken('paused');
		armTimeout('paused');
		// Guarded to match the tracking call above: `processId` is an optional prop, and
		// the action requires one.
		if (processId) {
			bridgeApi.sendAction({
				type: 'do_pause_process',
				process_id: processId,
			});
		}
	}, [armTimeout, bridgeApi, artifact.id, processId, processStatus]);

	const handleResume = useCallback(() => {
		trackEvent('Process Resumed', {
			artifact_id: artifact.id,
			...(processId ? { process_id: processId } : {}),
			...(processStatus ? { process_status: processStatus } : {}),
		});
		setActionTaken('resumed');
		armTimeout('resumed');
		if (processId) {
			bridgeApi.sendAction({
				type: 'do_resume_process',
				process_id: processId,
			});
		}
	}, [armTimeout, bridgeApi, artifact.id, processId, processStatus]);

	const title = TYPE_TITLES[artifact.type] ?? 'Monitor Subreddits';
	const description =
		'Polls the subreddits below on a heartbeat and surfaces posts that match your topic as insights.';
	const inFlight =
		actionTaken !== null && actionTaken !== 'failed' ? actionTaken : null;
	const isBlocked = inFlight !== null;
	const activeLike = isProcess
		? processStatus === 'active'
		: artifact.status === 'active';
	const pausedLike = isProcess
		? processStatus === 'paused'
		: artifact.status === 'paused';
	const canStart = isProcess
		? processStatus === 'proposed'
		: isArtifactActionable(artifact);
	const canActivate =
		canStart &&
		!isBlocked &&
		subreddits.length > 0 &&
		watchTopic.trim().length > 0;
	// Funnel sibling confirms a setup step ("Approve", to match other review
	// steps); standalone activates the monitor directly.
	const showSubmitButton = canStart && !isBlocked && isFunnelSibling;
	const showActivateButton = canStart && !isBlocked && !isFunnelSibling;

	const statusLabel =
		actionTaken === null && activeLike
			? 'Heartbeat active'
			: actionTaken === null && pausedLike
				? 'Paused'
				: null;

	const showAnyAction =
		showActivateButton ||
		showSubmitButton ||
		actionTaken !== null ||
		statusLabel;

	const actionRenderer = showAnyAction
		? () => (
				<>
					{inFlight && (
						<div
							className="flex items-center gap-2 rounded-full border
								border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-1
								text-sm font-medium text-[var(--text-secondary)]"
						>
							<ProgressCircle
								isIndeterminate
								size="S"
								aria-label={PROGRESS_LABEL[inFlight]}
							/>
							<span>{PROGRESS_LABEL[inFlight]}</span>
						</div>
					)}
					{actionTaken === 'failed' && (
						<div
							role="alert"
							className="flex items-center gap-2 rounded-full border
								border-red-300 bg-red-50 px-3 py-1 text-sm font-medium
								text-red-700"
						>
							<span>Couldn&apos;t reach the backend — try again.</span>
						</div>
					)}
					{statusLabel && (
						<div
							className="flex items-center gap-2 rounded-full border
								border-[var(--glass-border)] bg-[var(--glass-bg)] px-3 py-1
								text-sm font-medium text-[var(--text-secondary)]"
						>
							{activeLike ? (
								<span className="relative flex h-2 w-2">
									<span
										className="absolute inline-flex h-full w-full animate-ping
											rounded-full bg-green-500 opacity-75"
									/>
									<span
										className="relative inline-flex h-2 w-2 rounded-full
											bg-green-500"
									/>
								</span>
							) : (
								<span className="inline-flex h-2 w-2 rounded-full bg-gray-400" />
							)}
							<span>{statusLabel}</span>
						</div>
					)}
					{showActivateButton && (
						<Button
							type="button"
							variant="accent"
							onPress={handleActivate}
							isDisabled={!canActivate}
						>
							Start monitoring
						</Button>
					)}
					{showSubmitButton && (
						<Button
							type="button"
							variant="accent"
							onPress={handleSubmitStep}
							isDisabled={!canActivate}
						>
							Approve
						</Button>
					)}
					{activeLike && !isBlocked && (
						<Button type="button" variant="secondary" onPress={handlePause}>
							Pause monitor
						</Button>
					)}
					{pausedLike && !isBlocked && (
						<Button type="button" variant="accent" onPress={handleResume}>
							Resume monitor
						</Button>
					)}
				</>
			)
		: undefined;

	const overlayView = activatedView ?? submittedView;

	return (
		<ChatPanelLayout
			title={title}
			description={overlayView ? undefined : description}
			actionRenderer={overlayView ? undefined : actionRenderer}
			isLoading={false}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			{overlayView ?? <SubredditMonitorEditor artifact={artifact} />}
		</ChatPanelLayout>
	);
};
